<template>
    <div>
        <el-dialog :visible.sync="visible" width="80%"
                   :fullscreen="fullscreen"
                   :close-on-click-modal="false">
            <div slot="title" class="dialog-title">
                Risk Area assigned to {{source.abbreviation}}
                <span class="grey-text" v-text="source.name"></span>
                <el-button @click="fullscreen=!fullscreen" class="full-screen" icon="el-icon-full-screen"></el-button>
            </div>
            <div class="dialog-content" v-loading="loading">
                <el-table :data="data"
                          ref="sourceDetailsTable"
                          :default-sort="{prop: 'weight', order: 'descending'}"
                          style="width: 100%">
                    <el-table-column prop="name" label="Risk Area Name" width="300px"
                                     :sortable="true"></el-table-column>
                    <el-table-column prop="order" label="Order">
                        <template slot-scope="scope">
                            <el-input-number controls-position="right" size="mini"
                                             v-model.number="scope.row.order" :step="1"></el-input-number>
                        </template>
                    </el-table-column>
                    <el-table-column v-if="source.type === 'quant'" prop="weight" label="Weight" :sortable="true">
                        <template slot-scope="scope">
                            <el-select v-model="scope.row.weight" size="mini">
                                <el-option label="High" :value="20"></el-option>
                                <el-option label="Medium" :value="15"></el-option>
                                <el-option label="Low" :value="10"></el-option>
                                <el-option label="Exclude" :value="0"></el-option>
                            </el-select>
                        </template>
                    </el-table-column>
                    <el-table-column v-else prop="weight" label="Include / Exclude" :sortable="true">
                        <template slot-scope="scope">
                            <el-select v-model="scope.row.weight" size="mini">
                                <el-option label="Include" :value="10"></el-option>
                                <el-option label="Exclude" :value="0"></el-option>
                            </el-select>
                        </template>
                    </el-table-column>
                </el-table>
            </div>
            <div slot="footer" class="dialog-footer">
                <el-row>
                    <el-col :span="12" class="align-left enable-tooltip">
                        <el-tooltip v-if="source.type === 'qual'"
                                    :disabled="!(Boolean(data.length) || !Boolean(remainingRiskArea.length))"
                                    effect="dark"
                                    :content="(data.length)?'Only one source can be added in Qualitative Risk Area':'No more Qualitative Risk Area to add!'"
                                    placement="top">
                            <div>
                                <el-button
                                        :disabled="loading || Boolean(data.length) || !Boolean(remainingRiskArea.length)"
                                        size="medium"
                                        @click="openAddSourceToRiskAreaDialog()" type="primary">
                                    Add source to a Risk Area
                                </el-button>
                            </div>
                        </el-tooltip>
                        <el-tooltip v-else
                                    :disabled="Boolean(remainingRiskArea.length)"
                                    effect="dark"
                                    content="No more Risk Area to add"
                                    placement="top">
                            <div>
                                <el-button
                                        :disabled="loading || !Boolean(remainingRiskArea.length)"
                                        size="medium"
                                        @click="openAddSourceToRiskAreaDialog()" type="primary">
                                    Add source to a Risk Area
                                </el-button>
                            </div>
                        </el-tooltip>
                    </el-col>
                    <el-col :span="12">
                        <el-button size="medium" @click="visible = false">Close</el-button>
                        <el-button
                                :disabled="isChanged"
                                size="medium" @click="showSourceDetails(source)" type="info">Reset
                        </el-button>
                        <el-button
                                :disabled="isChanged"
                                type="primary" size="medium" @click="saveSourceDetailsDialog()">
                            Save
                        </el-button>
                    </el-col>
                </el-row>
            </div>
        </el-dialog>
        <el-dialog width="50%" :visible.sync="addSource.visible" append-to-body>
            <div slot="title" class="dialog-title">
                Add Risk Area to {{source.abbreviation}}:
                <span class="grey-text" v-text="source.name"></span>
            </div>
            <div class="add-source-dialog-content">
                <el-form ref="form" label-position="left" :model="addSource.form" label-width="200px" size="small">
                    <el-form-item label="Select Risk Area">
                        <el-select filterable default-first-option
                                   v-model="addSource.form.risk_area_id" placeholder="select risk area">
                            <el-option v-for="obj in remainingRiskArea"
                                       :label="obj.name"
                                       :value="obj.id"
                                       :key="obj.id">
                                {{obj.name}} <span class="source-abbr">({{obj.abbreviation}})</span>
                            </el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item v-if="source.type === 'quant'" label="Select Source Weight">
                        <el-select placeholder="please select weight" v-model="addSource.form.weight">
                            <el-option label="High" :value="20"></el-option>
                            <el-option label="Medium" :value="15"></el-option>
                            <el-option label="Low" :value="10"></el-option>
                            <el-option label="Exclude" :value="0"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item v-else label="Include / Exclude Source">
                        <el-select placeholder="select include / exclude" v-model="addSource.form.weight">
                            <el-option label="Include" :value="10"></el-option>
                            <el-option label="Exclude" :value="0"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Source Order">
                        <el-input-number v-model.number="addSource.form.order" :step="1"></el-input-number>
                    </el-form-item>
                </el-form>
            </div>
            <div slot="footer" class="dialog-footer">
                <el-button size="medium" @click="addSource.visible = false">Cancel</el-button>
                <el-button :disabled="!validateAddSourceForm" size="medium" type="primary"
                           @click="saveSourceToRiskArea()">
                    Save
                </el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
    import {createNamespacedHelpers} from 'vuex';
    import {API} from "@/api/index";

    const {mapState, mapActions} = createNamespacedHelpers('riskIndex');

    export default {
        name:     "SourceDetailDialogBySource",
        props:    {},
        data() {
            return {
                loading:    true,
                fullscreen: false,
                original:   {},
                source:     {},
                data:       [],
                visible:    false,
                addSource:  {
                    form:    {
                        weight:       '',
                        source_id:    '',
                        risk_area_id: '',
                        order:        0
                    },
                    visible: false
                }
            };
        },
        computed: {
            ...mapState(['riskAreaData', 'sourceData', 'sourceWeightData']),
            validateAddSourceForm: function () {
                return Object.values(this.addSource.form).every(x => typeof (x) === 'number');
            },
            isChanged() {
                return this.loading || (JSON.stringify(this.original[this.source.id]) === JSON.stringify(this.data));
            },
            remainingRiskArea() {
                if (!this.source['risk_areas'])
                    return [];

                if (this.source.type === 'qual') {
                    let allRiskAreaIds = new Set(Object.values(this.sourceData).map(row => row['risk_areas']).flat(1));
                    return Object.values(this.riskAreaData).filter(row => !allRiskAreaIds.has(row.id) && row.type === 'qual');
                }
                return Object.values(this.riskAreaData).filter(row => !this.source['risk_areas'].includes(row.id) && row.type === this.source.type);
            }
        },
        methods:  {
            ...mapActions([
                'refreshRiskIndexTableData',
                'loadSourceData',
                'loadSourceWeightData'
            ]),
            showSourceDetails:             async function (row) {
                let self = this;
                row      = self.sourceData[row.abbreviation]; // get the latest data
                self.createSourceDetailsData();
                self.data = JSON.parse(JSON.stringify(self.original[row.id]));
                //self.normalization.lower_bound = row.normalization.lower_bound;
                //self.normalization.upper_bound = row.normalization.upper_bound;
                self.visible = true;
                self.source  = row;
                self.loading = false;
                self.$nextTick(function () {
                    self.$refs.sourceDetailsTable.clearSort();
                    self.$refs.sourceDetailsTable.sort('weight', 'descending');
                });
            },
            createSourceDetailsData:       function () {
                let riskAreaSourceData = {};
                let riskAreaByID       = {};
                Object.values(this.riskAreaData).forEach(row => riskAreaByID[row.id] = row);
                Object.values(this.sourceData).forEach(obj => {
                    riskAreaSourceData[obj.id] = [];
                    obj['risk_areas'].forEach(id => {
                        let source_weight = this.sourceWeightData[`${id}-${obj.id}`] || {};
                        riskAreaSourceData[obj.id].push({
                            id:               obj.id,
                            risk_area_id:     id,
                            source_weight_id: source_weight.id,
                            name:             riskAreaByID[id]['name'],
                            order:            source_weight['order'],
                            weight:           source_weight['weight']
                        });
                    });
                });
                //copy object and save it as original to keep track of changes
                this.original = JSON.parse(JSON.stringify(riskAreaSourceData));
            },
            saveSourceDetailsDialog:       async function () {
                let self     = this;
                self.loading = true;
                let changed  = {};
                let original = self.original[self.source.id];
                // Find which object is changed and add to the changed list
                self.data.forEach((row, index) => {
                    let original_row = original[index];
                    if (original_row['order'] != row['order']) {
                        changed[`source-weight/${row['source_weight_id']}`] = {
                            'order':  row['order'],
                            'weight': row['weight']
                        };
                    }
                    if (original_row['weight'] != row['weight']) {
                        changed[`source-weight/${row['source_weight_id']}`] = {
                            'order':  row['order'],
                            'weight': row['weight']
                        };
                    }
                });
                for (let url in changed) {
                    try {
                        await self.$http.put(self.$appConfig.url.riskIndex + url, changed[url]);
                    } catch (ex) {
                        this.$notify.error({
                            title:   'Error',
                            message: 'Could not save the changes.'
                        });
                        console.error('Could not save the changes', ex);
                    }
                }
                await self.refreshRiskIndexTableData(true);
                await self.loadSourceData(true);
                await self.loadSourceWeightData(true);
                if (this.visible)
                    this.showSourceDetails(this.source);
                self.loading = false;
            },
            openAddSourceToRiskAreaDialog: function () {
                let dialog     = this.addSource;
                dialog.form    = {
                    weight:       '',
                    source_id:    this.source.id,
                    risk_area_id: '',
                    order:        0
                };
                dialog.visible = true;
            },
            saveSourceToRiskArea:          async function () {
                let dialog     = this.addSource;
                this.loading   = true;
                dialog.visible = false;
                try {
                    await API.riskIndex.sourceWeight.post([dialog.form]);
                    //await this.$http.post(this.$appConfig.url.riskIndex + 'source-weight', [dialog.form]);
                } catch (ex) {
                    this.$notify.error({
                        title:   'Error',
                        message: 'Could not save the changes.'
                    });
                    console.error('Could not save the changes', ex);
                    this.loading = false;
                }
                await this.refreshRiskIndexTableData(true);
                await this.loadSourceData(true);
                await this.loadSourceWeightData(true);
                if (this.visible)
                    this.showSourceDetails(this.source);
                this.loading = false;
            }
        },
        watch:    {
            loading: function (val) {
                this.$emit('update:loading', val);
            }
        }
    };
</script>
<style scoped lang="scss">
    @import "@/styles/_base.scss";

    .align-left {
        text-align: left;
    }

    .enable-tooltip .el-tooltip {
        display: inline;
    }

    .source-abbr {
        color: $color-grey-dark;
        padding-left: 5px;
    }

    .grey-text {
        color: $color-grey-dark;
        padding-left: 10px;
        font-size: 12px;
    }

    .full-screen {
        position: absolute;
        top: 20px;
        right: 45px;
        padding: 0;
        background: transparent;
        border: none;
        outline: none;
        cursor: pointer;
        font-size: 16px;
    }

    .dialog-content {
        .source-form {
            .el-form-item {
                margin-bottom: 5px;
            }

            .el-input {
                width: 130px;
            }
        }

        .el-divider {
            margin: 10px 0 0 0;
        }
    }
</style>